import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import SignInPage from './pages/SignInPage'
import SignUpPage from './pages/SignUpPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import TenantSelectionPage from './pages/TenantSelectionPage'
import WorkspaceSelectionPage from './pages/WorkspaceSelectionPage'
import DashboardPage from './pages/DashboardPage'
import TestPage from './pages/TestPage'
import ErrorBoundary from './app/components/utils/ErrorBoundary/ErrorBoundary'
import NetworkErrorBanner from './app/components/utils/NetworkErrorBanner/NetworkErrorBanner'
import './App.css'

// Lazy load workspace pages for better performance
const QAChatPage = lazy(() => import('./pages/QAChatPage'))
const MembersPage = lazy(() => import('./pages/MembersPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))

// Lazy load report pages for better performance
const QAReportPage = lazy(() => import('./pages/QAReportPage'))
const CSReportPage = lazy(() => import('./pages/CSReportPage'))
const ExportManagementPage = lazy(() => import('./pages/ExportManagementPage'))

// Loading component for lazy-loaded pages
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
)

// Placeholder for 404
const NotFoundPage = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-4xl font-bold mb-4">404</h1>
      <p className="text-gray-600">Page Not Found</p>
    </div>
  </div>
)

function App() {
  return (
    <ErrorBoundary>
      <NetworkErrorBanner />
      <Routes>
        {/* Auth Routes */}
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/login" element={<Navigate to="/sign-in" replace />} />
        <Route path="/sign-up" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/tenant-selection" element={<TenantSelectionPage />} />
        <Route path="/select-workspace" element={<WorkspaceSelectionPage />} />

        {/* Workspace Routes */}
        <Route
          path="/workspaces/:workspaceId/qa-chat"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <QAChatPage />
            </Suspense>
          }
        />
        <Route
          path="/workspaces/:workspaceId/members"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <MembersPage />
            </Suspense>
          }
        />
        <Route
          path="/workspaces/:workspaceId/settings"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <SettingsPage />
            </Suspense>
          }
        />
        <Route
          path="/workspaces/:workspaceId/profile"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ProfilePage />
            </Suspense>
          }
        />
        <Route path="/workspaces/:workspaceId" element={<Navigate to="qa-chat" replace />} />

        {/* Main Routes */}
        <Route path="/" element={<DashboardPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Data Center - Report & Export Routes */}
        <Route
          path="/data-center/reports/qa"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <QAReportPage />
            </Suspense>
          }
        />
        <Route
          path="/data-center/reports/cs"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <CSReportPage />
            </Suspense>
          }
        />
        <Route
          path="/data-center/reports/export-management"
          element={
            <Suspense fallback={<LoadingSpinner />}>
              <ExportManagementPage />
            </Suspense>
          }
        />

        {/* Test Route */}
        <Route path="/test" element={<TestPage />} />

        {/* Error Routes */}
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="/no-access" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}

export default App
